import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapboxMap.css';

const MapboxMap = ({ 
  center, 
  zoom = 15, 
  markers = [], 
  address = null,
  city = null,
  state = null,
  zipCode = null,
  style = { width: '100%', height: '400px' }
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const [geocodedCenter, setGeocodedCenter] = useState(null);
  const navigate = useNavigate();
  
  // Get Mapbox access token from environment variable
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  // Geocode address if coordinates are not provided or are default (0,0)
  useEffect(() => {
    const geocodeAddress = async () => {
      if (!accessToken) {
        console.error('Mapbox access token is not configured');
        return;
      }

      // If center is provided and valid (not 0,0), use it
      if (center && center.lat !== 0 && center.lng !== 0) {
        setGeocodedCenter(center);
        return;
      }

      // If address information is provided, geocode it
      if (address && city && state) {
        try {
          const query = encodeURIComponent(`${address}, ${city}, ${state} ${zipCode || ''}`);
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${accessToken}&limit=1`
          );
          const data = await response.json();
          
          if (data.features && data.features.length > 0) {
            const [lng, lat] = data.features[0].center;
            setGeocodedCenter({ lat, lng });
          } else {
            // Fallback to default Puerto Rico coordinates
            setGeocodedCenter({ lat: 18.2208, lng: -66.5901 });
          }
        } catch (error) {
          console.error('Error geocoding address:', error);
          // Fallback to default Puerto Rico coordinates
          setGeocodedCenter({ lat: 18.2208, lng: -66.5901 });
        }
      } else {
        // Fallback to center if valid, otherwise use default
        if (center && center.lat !== 0 && center.lng !== 0) {
          setGeocodedCenter(center);
        } else {
          setGeocodedCenter({ lat: 18.2208, lng: -66.5901 });
        }
      }
    };

    geocodeAddress();
  }, [center, address, city, state, zipCode, accessToken]);

  // Initialize map
  useEffect(() => {
    if (!accessToken) {
      console.error('Mapbox access token is not configured. Please set REACT_APP_MAPBOX_ACCESS_TOKEN in your .env file.');
      return;
    }

    if (!geocodedCenter || map.current) return;

    mapboxgl.accessToken = accessToken;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [geocodedCenter.lng, geocodedCenter.lat],
      zoom: zoom
    });

    map.current.on('load', () => {
      setMapReady(true);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [accessToken, geocodedCenter, zoom]);

  // Add markers when map is ready
  useEffect(() => {
    if (!mapReady || !map.current) return;

    // Remove existing markers using stored references
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // If markers array is provided (for list views)
    if (markers && markers.length > 0) {
      markers.forEach((markerData) => {
        if (markerData.coordinates && markerData.coordinates.lat !== 0 && markerData.coordinates.lng !== 0) {
          const popupContent = document.createElement('div');
          popupContent.className = 'mapbox-popup';

          const title = document.createElement('h3');
          title.textContent = markerData.title || '';
          popupContent.appendChild(title);

          const price = document.createElement('p');
          price.textContent = markerData.price ? `$${markerData.price.toLocaleString()}` : '';
          popupContent.appendChild(price);

          const popup = new mapboxgl.Popup({ offset: 25 }).setDOMContent(popupContent);

          const marker = new mapboxgl.Marker()
            .setLngLat([markerData.coordinates.lng, markerData.coordinates.lat])
            .setPopup(popup)
            .addTo(map.current);

          // Add click handler to navigate to property detail
          marker.getElement().addEventListener('click', () => {
            if (markerData.onClick) {
              markerData.onClick();
            } else if (markerData.id) {
              navigate(`/property/${markerData.id}`);
            }
          });

          // Store marker reference for cleanup
          markersRef.current.push(marker);
        }
      });

      // Fit map to show all markers
      if (markers.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        markers.forEach(markerData => {
          if (markerData.coordinates && markerData.coordinates.lat !== 0 && markerData.coordinates.lng !== 0) {
            bounds.extend([markerData.coordinates.lng, markerData.coordinates.lat]);
          }
        });
        map.current.fitBounds(bounds, { padding: 50 });
      }
    } else if (geocodedCenter) {
      // Single marker for property detail view
      const marker = new mapboxgl.Marker()
        .setLngLat([geocodedCenter.lng, geocodedCenter.lat])
        .addTo(map.current);
      
      markersRef.current.push(marker);
    }
  }, [mapReady, markers, geocodedCenter, navigate]);

  if (!accessToken) {
    return (
      <div style={style} className="mapbox-error">
        <p>Map configuration error. Please configure REACT_APP_MAPBOX_ACCESS_TOKEN.</p>
      </div>
    );
  }

  return <div ref={mapContainer} style={style} className="mapbox-map" />;
};

export default MapboxMap;
